import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, payOrder } from '../../services/api';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import useToast from '../../hooks/useToast';

const PaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const inputRef = useRef(null);
  const paymentTriggered = useRef(false);
  
  const [attendeeId, setAttendeeId] = useState('');

  // ✅ React Query: Fetch order details
  const {
    data: order,
    isLoading,
    isError,
    refetch: refetchOrder,
  } = useApiQuery(
    ['order', id],
    () => getOrder(id).then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds
      onError: () => {
        showError('Order not found');
        navigate('/vendor/orders');
      },
    }
  );

  // ✅ React Query: Pay order mutation
  const payOrderMutation = useApiMutation(
    (attendeeId) => payOrder(id, attendeeId).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Payment successful!');
        navigate('/vendor/orders');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Payment failed');
        paymentTriggered.current = false;
        // Refocus input for next scan
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.value = '';
        }
      },
    }
  );

  // ✅ Auto-focus the input when page loads
  useEffect(() => {
    if (!isLoading && order && order.status === 'pending') {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isLoading, order]);

  // ✅ Process payment
  const processPayment = useCallback((idToPay) => {
    if (!idToPay || idToPay.trim() === '') {
      showError('Attendee ID is missing.');
      return;
    }
    payOrderMutation.mutate(idToPay);
  }, [payOrderMutation, showError]);

  // ✅ Handle keydown (Enter key)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = e.target.value.trim();
      if (val && !payOrderMutation.isPending && !paymentTriggered.current) {
        paymentTriggered.current = true;
        setAttendeeId(val);
        processPayment(val);
        e.target.value = '';
      }
    }
  }, [payOrderMutation.isPending, processPayment]);

  // ✅ Handle manual pay
   // ✅ Handle manual pay
  const handleManualPay = useCallback(async () => {
    const val = attendeeId.trim();
    if (!val) {
      showError('Please enter attendee ID');
      return;
    }
    if (payOrderMutation.isPending) return;
    paymentTriggered.current = true;
    processPayment(val);
  }, [attendeeId, payOrderMutation.isPending, processPayment, showError]);

  // ✅ Handle auto-detect from scanner
  const handleChange = useCallback((e) => {
    const value = e.target.value.trim();
    setAttendeeId(value);
  }, []);

  // ✅ Handle re-scan
  const handleRescan = useCallback(() => {
    setAttendeeId('');
    paymentTriggered.current = false;
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.value = '';
    }
  }, []);


  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading order...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError || !order) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Order not found</p>
        <button
          onClick={() => navigate('/vendor/orders')}
          className="mt-4 text-[#1471b9] hover:underline"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const isProcessing = payOrderMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Order Summary */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-[#2b3445]">Order Summary</h2>
        <div className="mt-4 space-y-2">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span>{item.foodItemId?.name || 'Item'} x{item.quantity}</span>
              <span>₹{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="border-t border-[#dae1e7] pt-2 mt-2 flex justify-between font-bold">
            <span>Total</span>
            <span>₹{order.total.toFixed(2)}</span>
          </div>
          <div className="text-sm text-[#708090]">
            Status:{' '}
            <span className={`font-medium ${
              order.status === 'paid' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {order.status}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] p-6">
        {order.status === 'pending' ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                {isProcessing ? '⏳ Processing payment...' : '🔍 Scanning badge...'}
              </label>
              <input
                ref={inputRef}
                type="text"
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                placeholder={isProcessing ? 'Please wait...' : 'Scan badge or enter ID manually'}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                disabled={isProcessing}
                autoFocus
              />
              <p className="text-xs text-[#708090] mt-1">
                Payment will start automatically when ID is detected.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleManualPay}
                disabled={isProcessing || !attendeeId.trim()}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-[8px] font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {isProcessing ? 'Processing...' : 'Pay Now (Manual)'}
              </button>
              <button
                onClick={handleRescan}
                className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] font-bold hover:opacity-90 transition-colors"
              >
                🔄 Re‑scan
              </button>
            </div>
          </>
        ) : (
          <div className="text-center text-green-600 font-semibold">
            ✅ This order is already paid.
          </div>
        )}
      </div>
    </div>
  );
};
export default PaymentPage;
